import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { useScheduleContext } from "../hooks/context/useScheduleContext";
import { useFilters } from "../../../../utils/hooks/useFilters";
import useApi from "../../../../utils/api";
import { getErrorMessage } from "../../../../utils/helpers/errors";
import { withLoadingToast } from "../helpers/withLoadingToast";

export const useCopyMutations = ({ values, onSuccess }: { values: any; onSuccess: (response: any) => void }) => {
    const { t } = useTranslation();
    const { authPost } = useApi();
    const { type, templates } = useScheduleContext();
    const [filters] = useFilters();

    const templateId = useMemo(() => {
        return filters?.template?.id || templates?.[0]?.id;
    }, [filters?.template?.id, templates]);

    const cellMutation = useMutation({
        ...withLoadingToast(t("copying")),
        mutationFn: (data: any) => {
            const endpoint = type === "team"
                ? `/schedules/v2/copy/${values?.event}`
                : type === "home"
                    ? `/my/schedules/v2/copy/${values?.event}`
                    : (type === "template" || type === "profile")
                        ? `/shift-templates/v2/${templateId}/copy/${values?.event}`
                        : `/my/shift-templates/v2/${templateId}/copy/${values?.event}`;
            return authPost(endpoint, { data });
        },
        onSuccess,
        onError: (err) => getErrorMessage(err, t),
    });

    const userMutation = useMutation({
        ...withLoadingToast(t("copying")),
        mutationFn: (data: any) => {
            const endpoint = type === "team"
                ? `/schedules/v2/copy/user/${values?.user?.id}`
                : type === "home"
                    ? "/my/schedules/v2/copy/user"
                    : (type === "template" || type === "profile")
                        ? `/shift-templates/v2/${templateId}/copy/user/${values?.user?.id}`
                        : `/my/shift-templates/v2/${templateId}/copy/user`;
            return authPost(endpoint, { data });
        },
        onSuccess,
        onError: (err) => getErrorMessage(err, t),
    });

    const bulkMutation = useMutation({
        ...withLoadingToast(t("copying")),
        mutationFn: (data: any) => {
            const endpoint = type === "team"
                ? "/schedules/v2/copy/bulk"
                : type === "home"
                    ? "/my/schedules/v2/copy/bulk"
                    : (type === "template" || type === "profile")
                        ? `/shift-templates/v2/${templateId}/copy/day`
                        : `/my/shift-templates/v2/${templateId}/copy/day`;
            return authPost(endpoint, { data });
        },
        onSuccess,
        onError: (err) => getErrorMessage(err, t),
    });

    const isLoading = cellMutation.isPending || userMutation.isPending || bulkMutation.isPending;

    return { cellMutation, userMutation, bulkMutation, isLoading };
};
